import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Divider,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LuEye } from "react-icons/lu";
import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { uploadUnlistedInvestorDocs } from "../../../redux/rm/rmSlice";
import { FileUpload } from "@mui/icons-material";

const DocumentModal = ({
  open,
  handleClose,
  documentUrls,
  userData,
  fetchUnlistedInvestors,
}: any) => {
  const dispatch = useDispatch<any>();
  const documents = [
    { name: "CML Document", key: "cmlDoc" },
    { name: "PAN Card", key: "panDoc" },
    { name: "Cancelled Cheque", key: "cancelledChequeDoc" },
    { name: "CML Document 2", key: "cmlDoc2" },
  ];

  const [expandedDocs, setExpandedDocs] = useState<{ [key: string]: boolean }>({
    cmlDoc: false,
    panDoc: false,
    cancelledChequeDoc: false,
    cmlDoc2: false,
  });
  const [selectedFiles, setSelectedFiles] = useState<{
    [key: string]: File | null;
  }>({
    cmlDoc: null,
    panDoc: null,
    cancelledChequeDoc: null,
    cmlDoc2: null,
  });
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({
    cmlDoc: null,
    panDoc: null,
    cancelledChequeDoc: null,
    cmlDoc2: null,
  });

  const handleDownload = (url: string) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = "";
    link.click();
  };

  const handleToggleExpand = (docKey: string) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [docKey]: !prev[docKey],
    }));
  };

  const handleFileChange = (
    docKey: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    setSelectedFiles((prev) => ({
      ...prev,
      [docKey]: file,
    }));
  };

  const handleClearFile = (docKey: string) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [docKey]: null,
    }));
    setExpandedDocs((prev) => ({
      ...prev,
      [docKey]: false,
    }));
    if (fileInputRefs.current[docKey]) {
      fileInputRefs.current[docKey]!.value = "";
    }
  };

  const handleUploadAll = () => {

    // Create FormData
    const formData = new FormData();
    formData.append("investorId", userData?.id || "");
        formData.append("orderId", "");
    formData.append("investorPAN", userData?.pan || "");
    if (selectedFiles.panDoc) {
      formData.append("panDoc", selectedFiles.panDoc);
    }
    if (selectedFiles.cmlDoc) {
      formData.append("cmlDoc", selectedFiles.cmlDoc);
    }
    if (selectedFiles.cancelledChequeDoc) {
      formData.append("cancelledChequeDoc", selectedFiles.cancelledChequeDoc);
    }
    if (selectedFiles.cmlDoc2) {
      formData.append("cmlDoc2", selectedFiles.cmlDoc2);
    }

    // Log FormData entries for debugging
    console.log("FormData contents:",formData);
    // Log FormData entries for debugging

    for (const [key, value] of Array.from(formData.entries())) {
      console.log(`${key}:`, value);
    }

    // Dispatch upload action
    dispatch(uploadUnlistedInvestorDocs(formData))
      .then(async (response: any) => {
        if (response?.error?.message) {
          toast.error(
            response?.payload?.message || "Failed to upload documents"
          );
          return;
        }
        toast.success("Order updated successfully");
        fetchUnlistedInvestors(); // Refresh the list of investors
        handleClose(); // Close modal on successful upload
      })
      .catch((error: any) => {
        console.error("Error executing order:", error);
        toast.error("Error executing order");
      });

    // Reset state
    setSelectedFiles({
      cmlDoc: null,
      panDoc: null,
      cancelledChequeDoc: null,
      cmlDoc2: null,
    });
    setExpandedDocs({
      cmlDoc: false,
      panDoc: false,
      cancelledChequeDoc: false,
      cmlDoc2: false,
    });
    Object.values(fileInputRefs.current).forEach((ref) => {
      if (ref) ref.value = "";
    });
  };

  const handleCancelAll = () => {
    setSelectedFiles({
      cmlDoc: null,
      panDoc: null,
      cancelledChequeDoc: null,
      cmlDoc2: null,
    });
    setExpandedDocs({
      cmlDoc: false,
      panDoc: false,
      cancelledChequeDoc: false,
      cmlDoc2: false,
    });
    Object.values(fileInputRefs.current).forEach((ref) => {
      if (ref) ref.value = "";
    });
  };

  const triggerFileInput = (docKey: string) => {
    fileInputRefs.current[docKey]?.click();
  };

  const hasSelectedFiles = Object.values(selectedFiles).some(
    (file) => file !== null
  );

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 24,
        p: 4,
        display: open ? "block" : "none",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Documents</Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {documentUrls &&
          documents.map((doc, index) => (
            <Box key={doc.key}>
              <ListItem>
                <ListItemText primary={doc.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    color="primary"
                    style={{ marginRight: "4px" }}
                    onClick={() => handleToggleExpand(doc.key)}
                  >
                    <FileUpload />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="primary"
                    onClick={() => handleDownload(documentUrls[doc.key])}
                    disabled={!documentUrls[doc.key]}
                  >
                    <LuEye />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Collapse in={expandedDocs[doc.key]}>
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: selectedFiles[doc.key]
                      ? "primary.main"
                      : "grey.400",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    bgcolor: "grey.100",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "grey.200",
                    },
                    mx: 2,
                    mb: 2,
                  }}
                >
                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[doc.key] = el)}
                    onChange={(e) => handleFileChange(doc.key, e)}
                    style={{ display: "none" }}
                  />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={() => triggerFileInput(doc.key)}
                    >
                      Choose File
                    </Button>
                    {selectedFiles[doc.key] && (
                      <IconButton
                        color="error"
                        onClick={() => handleClearFile(doc.key)}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color={
                      selectedFiles[doc.key] ? "primary.main" : "text.secondary"
                    }
                    mt={1}
                  >
                    {selectedFiles[doc.key]
                      ? `Selected: ${selectedFiles[doc.key]!.name}`
                      : "No file selected"}
                  </Typography>
                </Box>
              </Collapse>
              {index < documents.length - 1 && <Divider />}
            </Box>
          ))}
      </List>

      {hasSelectedFiles && (
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button variant="outlined" onClick={handleCancelAll}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadAll}
            disabled={!hasSelectedFiles}
          >
            Upload
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DocumentModal;
